import { useEffect, useState, type ReactNode } from 'react';
import { cx } from '../cx';
import { Button } from './Button';
import { Modal } from './Drawer';
import { Textarea } from './Form';
import { Icon } from './Icon';
import { Label } from './Typography';

export interface ForwardPerson { id: string; name: string; title?: string | null }

/**
 * Encaminhar uma tela a colegas: escolher as pessoas de um clique, um recado opcional e enviar. Serve aos dois temas.
 * É um recado só, sem resposta. Quem busca as pessoas e envia é quem usa o componente (`people`, `onSend`).
 */
export function Forward({ open, onClose, what, context, people, onSend, busy, error, noteMax = 280 }: {
  open: boolean;
  onClose: () => void;
  /** o que está sendo encaminhado: o nome do tema, da hipótese */
  what: ReactNode;
  /** de onde é: "Rastro · massas" */
  context?: string;
  people: ForwardPerson[] | undefined;
  onSend: (userIds: string[], note: string) => Promise<unknown>;
  busy?: boolean;
  error?: string | null;
  noteMax?: number;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [sentTo, setSentTo] = useState<string[] | null>(null);
  useEffect(() => { if (open) { setPicked([]); setNote(''); setSentTo(null); } }, [open]);
  const toggle = (id: string) => setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const first = (id: string) => people?.find((p) => p.id === id)?.name.split(' ')[0] ?? '';
  const send = () => void onSend(picked, note.trim()).then(() => setSentTo(picked)).catch(() => undefined);
  return (
    <Modal open={open} onClose={onClose} eyebrow={context ? `encaminhar · ${context}` : 'encaminhar'} title={what}
      footer={sentTo ? <Button onClick={onClose}>fechar</Button> : <Button variant="primary" disabled={busy || picked.length === 0} onClick={send}><Icon name="send" size={14} /> {busy ? 'enviando' : 'encaminhar'}</Button>}>
      {sentTo ? <p className="rr-forward__done">Enviado para {sentTo.map(first).join(', ')}.</p> : (
        <div className="rr-forward">
          <Label>para quem</Label>
          {!people ? <p className="rr-faint">carregando a equipe</p> : people.length === 0 ? <p className="rr-faint">Ainda não há outra pessoa da equipe para receber.</p> : (
            <div className="rr-chip-row">
              {people.map((p) => <button key={p.id} type="button" aria-pressed={picked.includes(p.id)} className={cx('rr-chip', picked.includes(p.id) && 'rr-chip--on')} onClick={() => toggle(p.id)}>{p.name}{p.title && <small>{p.title}</small>}</button>)}
            </div>
          )}
          <Label>recado</Label>
          <Textarea className="rr-forward__note" rows={3} maxLength={noteMax} value={note} onChange={(e) => setNote(e.target.value)} placeholder="opcional" aria-label="recado" />
          {error && <p className="rr-forward__error" role="alert">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
